import "../../../shared/styles/form.css";
import "../styles.css";

import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  notification,
  Select,
  Space,
} from "antd";
import { RcFile } from "antd/lib/upload";
import Upload from "antd/lib/upload/Upload";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import UploadOutlined from "@ant-design/icons/lib/icons/UploadOutlined";

import { createServiceTypeFileApi } from "../../../services/filesApi";
import {
  createServiceType,
  getNextSortOrder,
  getServiceType,
  patchServiceType,
} from "../../../services/serviceTypeApi";
import { CALCULATOR_TYPE } from "../../../shared/constants/CalculatorType";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import ServiceTypeDTO from "../../../shared/dtos/serviceTypeDTO";
import ContainerLayout from "../../common/containerLayout";

const SERVICE_TYPE_CATEGORY_OPTIONS = [
  { label: "Normal", value: "NORMAL" },
  { label: "Tier", value: "TIER" },
];

export const ServiceTypeForm = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  let { serviceTypeId } = useParams();

  const [loading, setLoading] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isManagerFileLoading, setIsManagerFileLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [managerFileUrl, setManagerFileUrl] = useState("");
  const currentImage = Form.useWatch("image", form);
  const currentManagerImage = Form.useWatch("managerImage", form);
  const selectedType = Form.useWatch("type", form);

  const isTier = String(selectedType ?? "").toUpperCase() === "TIER";

  const getServiceTypeData = async (serviceTypeId: number) => {
    await getServiceType(serviceTypeId).then((res: any) => {
      const imageUrl = res.image || "";
      const managerImageUrl = res.managerImage || "";
      setFileUrl(imageUrl);
      setManagerFileUrl(managerImageUrl);
      form.setFieldsValue({
        name: res.name,
        calculator: res.calculator,
        capacity: res.capacity,
        luggageCount: res.luggageCount,
        distance: res.distance,
        fixed: res.fixed,
        minute: res.minute,
        price: res.price,
        image: imageUrl,
        managerImage: managerImageUrl,
        type: res.type || "NORMAL",
        serviceClass: res.serviceClass,
        internationalPercentage: res.internationalPercentage ?? 100,
        sortOrder: res.sortOrder ?? 999999,
      });
    });
  };

  useEffect(() => {
    if (serviceTypeId) {
      getServiceTypeData(+serviceTypeId);
    } else {
      getNextSortOrder().then((res: { nextSortOrder: number }) => {
        form.setFieldValue("sortOrder", res.nextSortOrder);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinish = async (values: any) => {
    const {
      calculator,
      capacity,
      distance,
      fixed,
      image,
      managerImage,
      luggageCount,
      minute,
      name,
      price,
      type,
      internationalPercentage,
      sortOrder,
    } =
      values;
    const serviceClass = "DRIVER";

    const serviceTypeDTO: ServiceTypeDTO = {
      name: name,
      calculator: calculator,
      capacity: capacity,
      luggageCount: luggageCount,
      distance: isTier ? undefined : distance,
      fixed: isTier ? undefined : fixed,
      minute: isTier ? undefined : minute,
      price: isTier ? undefined : price,
      type: type || "NORMAL",
      internationalPercentage: isTier ? (internationalPercentage ?? 100) : undefined,
      sortOrder: sortOrder ?? 999999,
    };
    serviceTypeDTO.image = fileUrl || image;
    serviceTypeDTO.managerImage = managerFileUrl || managerImage;

    try {
      setLoading(true);
      if (serviceTypeId) {
        await patchServiceType(serviceTypeDTO, +serviceTypeId).then(() => {
          notification.success({
            message: `Tipo de Servicio #${serviceTypeId}`,
            description: "Se ha editado exitosamente",
            placement: "bottom",
            key: "global",
          });
        });
      } else {
        serviceTypeDTO.serviceClass = serviceClass;
        await createServiceType(serviceTypeDTO).then(() => {
          notification.success({
            message: "Tipo de Servicio",
            description: "Se ha creado exitosamente",
            placement: "bottom",
            key: "global",
          });
        });
      }

      navigate(WebRoutes.ServiceTypeRoute, {
        replace: true,
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message
        || error?.response?.data?.errors?.minute
        || "Error al guardar el tipo de servicio";

      const errors = error?.response?.data?.errors;
      const description = errors
        ? Object.values(errors).join(". ")
        : undefined;

      notification.error({
        message,
        description,
        placement: "bottom",
        key: "global",
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (file: RcFile) => {
    setIsFileLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    await createServiceTypeFileApi(formData).then((res: any) => {
      setFileUrl(res.location);
      form.setFieldValue("image", res.location);
      setIsFileLoading(false);
      notification.success({
        message: "La imagen se ha cargado exitosamente",
        placement: "bottom",
        key: "global",
      });
    });
    return false;
  };

  const uploadManagerFile = async (file: RcFile) => {
    setIsManagerFileLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    await createServiceTypeFileApi(formData).then((res: any) => {
      setManagerFileUrl(res.location);
      form.setFieldValue("managerImage", res.location);
      setIsManagerFileLoading(false);
      notification.success({
        message: "La imagen para manager se ha cargado exitosamente",
        placement: "bottom",
        key: "global",
      });
    });
    return false;
  };

  return (
    <ContainerLayout showNotification={false} title="Nuevo tipo de servicio">
      <Col span={24}>
        <Form
          form={form}
          name="basic"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 10 }}
          onFinish={onFinish}
          initialValues={{
            type: "NORMAL",
          }}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Nombre"
            rules={[
              { required: true, message: "Por favor ingresa un nombre!" },
            ]}
          >
            <Input disabled={serviceTypeId ? true : false} />
          </Form.Item>

          <Form.Item
            name="capacity"
            label="Capacidad de pasajeros"
            rules={[
              {
                required: true,
                message: "Por favor ingresa una capacidad!",
              },
            ]}
          >
            <InputNumber<number>
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
              min={0}
              className="form-input-container"
            />
          </Form.Item>

          {!isTier && (
            <>
              <Form.Item
                label="Precio"
                name="price"
                rules={[
                  { required: true, message: "Por favor ingresa un precio!" },
                ]}
              >
                <InputNumber<number>
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                  className="form-input-container"
                />
              </Form.Item>

              <Form.Item
                label="Precio Fijo"
                name="fixed"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa un precio fijo!",
                  },
                ]}
              >
                <InputNumber<number>
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                  className="form-input-container"
                />
              </Form.Item>

              <Form.Item
                label="Minutos"
                name="minute"
                rules={[
                  { required: true, message: "Por favor ingresa un tiempo!" },
                ]}
              >
                <InputNumber<number>
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                  step={0.5}
                  precision={2}
                  className="form-input-container"
                />
              </Form.Item>

              <Form.Item
                label="Distancia"
                name="distance"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa una distancia!",
                  },
                ]}
              >
                <InputNumber<number>
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                  className="form-input-container"
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="calculator"
            label="Calculador"
            rules={[
              {
                required: true,
                message: "Por favor ingresa un calculador!",
              },
            ]}
          >
            <Select
              placeholder="Seleccione un calculador"
              showSearch
              options={CALCULATOR_TYPE}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Tipo"
            rules={[
              {
                required: true,
                message: "Por favor selecciona un tipo!",
              },
            ]}
          >
            <Select
              placeholder="Seleccione el tipo"
              options={SERVICE_TYPE_CATEGORY_OPTIONS}
              optionFilterProp="label"
            />
          </Form.Item>

          {isTier && (
            <Form.Item
              label="% Internacional"
              name="internationalPercentage"
              rules={[
                {
                  required: true,
                  message: "Por favor ingresa el % internacional!",
                },
              ]}
            >
              <InputNumber<number>
                min={0}
                max={999.99}
                step={0.5}
                precision={2}
                className="form-input-container"
                addonAfter="%"
              />
            </Form.Item>
          )}

          <Form.Item
            label="Orden"
            name="sortOrder"
            rules={[
              {
                required: true,
                message: "Por favor ingresa el orden!",
              },
            ]}
          >
            <InputNumber<number>
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
              min={0}
              precision={0}
              className="form-input-container"
            />
          </Form.Item>

          <Form.Item
            name="luggageCount"
            label="Numero de maletas"
            rules={[
              {
                required: true,
                message: "Por favor ingresa el numero de maletas!",
              },
            ]}
          >
            <InputNumber<number>
              parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
              min={0}
              precision={0}
              className="form-input-container"
            />
          </Form.Item>

          <Form.Item label="Imagen del tipo de servicio">
            <Upload
              listType="picture"
              beforeUpload={(res) => uploadFile(res)}
              maxCount={1}
            >
              <Button loading={isFileLoading} icon={<UploadOutlined />}>
                Cargar archivo
              </Button>
            </Upload>
          </Form.Item>

          {!!(currentImage || fileUrl) && (
            <Form.Item label="Vista previa imagen del tipo de servicio">
              <img
                src={currentImage || fileUrl}
                alt="Imagen del tipo de servicio"
                style={{ width: 140, height: 80, objectFit: "cover", borderRadius: 8 }}
              />
            </Form.Item>
          )}

          <Form.Item label="Imagen para manager">
            <Upload
              listType="picture"
              beforeUpload={(res) => uploadManagerFile(res)}
              maxCount={1}
            >
              <Button loading={isManagerFileLoading} icon={<UploadOutlined />}>
                Cargar archivo
              </Button>
            </Upload>
          </Form.Item>

          {!!(currentManagerImage || managerFileUrl) && (
            <Form.Item label="Vista previa imagen para manager">
              <img
                src={currentManagerImage || managerFileUrl}
                alt="Imagen para manager"
                style={{ width: 140, height: 80, objectFit: "cover", borderRadius: 8 }}
              />
            </Form.Item>
          )}

          <Form.Item
            name="image"
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item name="managerImage" hidden>
            <Input />
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 15, span: 16 }}>
            <Space direction="horizontal">
              <Button type="primary" htmlType="submit" loading={loading}>
                Guardar
              </Button>
              <Button
                onClick={() =>
                  navigate(WebRoutes.ServiceTypeRoute, {
                    replace: true,
                  })
                }
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Col>
    </ContainerLayout>
  );
};

export default ServiceTypeForm;
